import React, { useState, useEffect } from 'react';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import { DollarSign, Car, Info, CreditCard, Receipt, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';

// Import the stamp duty calculator logic and data
import { 
  calculateStampDuty, 
  formatCurrency, 
  StampDutyInputs, 
  StampDutyResult 
} from '../utils/stampDutyCalculator';
import { 
  STATE_OPTIONS,
  VEHICLE_TYPES,
  CYLINDER_OPTIONS,
  CO2_OPTIONS,
  CONCESSION_OPTIONS 
} from '../data/stampDutyRules';

const StampDutyCalculator: React.FC = () => {
  // State variables for form inputs
  const [state, setState] = useState('NSW');
  const [vehicleValue, setVehicleValue] = useState('');
  const [vehicleType, setVehicleType] = useState('passenger');
  const [isNew, setIsNew] = useState(true);
  const [engineInfo, setEngineInfo] = useState('');
  const [concessions, setConcessions] = useState<string[]>([]);
  const [result, setResult] = useState<StampDutyResult | null>(null);
  const [engineInfoNeeded, setEngineInfoNeeded] = useState(false);
  const [engineInfoType, setEngineInfoType] = useState<'cylinders' | 'emissions'>('cylinders');
  const [engineOptions, setEngineOptions] = useState<{value: string, label: string}[]>([]);

  // Update engine info requirements based on selected state and vehicle type
  useEffect(() => {
    if (state === 'QLD' && vehicleType === 'passenger') {
      setEngineInfoNeeded(true);
      setEngineInfoType('cylinders');
      setEngineOptions(CYLINDER_OPTIONS);
      setEngineInfo(CYLINDER_OPTIONS[0].value); // Default to first option
    } else if (state === 'ACT' && vehicleType === 'passenger') {
      setEngineInfoNeeded(true);
      setEngineInfoType('emissions');
      setEngineOptions(CO2_OPTIONS);
      setEngineInfo(CO2_OPTIONS[0].value); // Default to first option
    } else {
      setEngineInfoNeeded(false);
      setEngineInfo('');
    }
  }, [state, vehicleType]);

  // Handle concession checkbox changes
  const handleConcessionChange = (value: string) => {
    if (concessions.includes(value)) {
      setConcessions(concessions.filter(item => item !== value));
    } else {
      setConcessions([...concessions, value]);
    }
  };

  // Calculate stamp duty
  const calculateDuty = () => {
    if (!vehicleValue || parseFloat(vehicleValue) <= 0) {
      alert('Please enter a valid vehicle value.');
      return;
    }

    const inputs: StampDutyInputs = {
      state,
      vehicleValue: parseFloat(vehicleValue),
      vehicleType,
      isNew,
      engineInfo: engineInfoNeeded ? engineInfo : undefined,
      concessions
    };

    const dutyResult = calculateStampDuty(inputs);
    setResult(dutyResult);
  };

  return (
    <>
      <SEOHead
        title="Australian Vehicle Stamp Duty Calculator | Calculate Registration Transfer Fees"
        description="Calculate vehicle stamp duty and transfer fees for all Australian states and territories including NSW, VIC, QLD, WA, SA, TAS, ACT and NT. Get accurate estimates based on your location, vehicle type and value."
        keywords="vehicle stamp duty calculator australia, NSW stamp duty, VIC motor vehicle duty, QLD vehicle registration duty, WA vehicle transfer fee, SA car stamp duty, TAS vehicle duty, ACT vehicle duty, NT stamp duty, car transfer duty, motor vehicle duty, EV stamp duty exemptions"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Australian Vehicle Stamp Duty Calculator",
              "description": "Calculate the stamp duty payable when registering or transferring ownership of vehicles in all Australian states and territories including NSW, VIC, QLD, WA, SA, TAS, ACT and NT.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": {
                "@type": "Country",
                "name": "Australia"
              },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Australian Vehicle Stamp Duty Calculator"
        description="Calculate stamp duty and transfer fees for vehicle registrations in all Australian states and territories: New South Wales (NSW), Victoria (VIC), Queensland (QLD), Western Australia (WA), South Australia (SA), Tasmania (TAS), Australian Capital Territory (ACT), and Northern Territory (NT)."
        icon={Car}
        result={result ? `Stamp Duty Payable: ${formatCurrency(result.dutyAmount)}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                This calculator provides estimates of vehicle stamp duty based on current rates and rules. 
                Actual amounts payable may vary. Please check with your state or territory revenue office for exact figures.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* State/Territory Selection */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">State/Territory</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                {STATE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Vehicle Value */}
            <Input
              label="Vehicle Value"
              value={vehicleValue}
              onChange={setVehicleValue}
              unit="$"
              placeholder="e.g. 25000"
            />

            {/* Vehicle Type */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              >
                {VEHICLE_TYPES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* New or Used */}
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">New or Used Vehicle</label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    className="form-radio h-4 w-4 text-blue-600"
                    checked={isNew} 
                    onChange={() => setIsNew(true)}
                  />
                  <span className="ml-2">New</span>
                </label>
                <label className="inline-flex items-center">
                  <input 
                    type="radio" 
                    className="form-radio h-4 w-4 text-blue-600"
                    checked={!isNew} 
                    onChange={() => setIsNew(false)}
                  />
                  <span className="ml-2">Used</span>
                </label>
              </div>
            </div>

            {/* Engine/Emissions Information (conditional) */}
            {engineInfoNeeded && (
              <div className="form-control w-full md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {engineInfoType === 'cylinders' ? 'Number of Cylinders' : 'CO₂ Emissions Category'}
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  value={engineInfo}
                  onChange={(e) => setEngineInfo(e.target.value)}
                >
                  {engineOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {engineInfoType === 'cylinders' 
                    ? 'Queensland uses cylinder count to determine duty rates.'
                    : 'ACT uses CO₂ emissions categories to determine duty rates.'}
                </p>
              </div>
            )}

            {/* Concessions Section */}
            <div className="form-control w-full md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Applicable Concessions</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {CONCESSION_OPTIONS.filter(option => option.value !== 'none').map(option => (
                  <label key={option.value} className="inline-flex items-center">
                    <input 
                      type="checkbox" 
                      className="form-checkbox h-4 w-4 text-blue-600"
                      checked={concessions.includes(option.value)} 
                      onChange={() => handleConcessionChange(option.value)}
                    />
                    <span className="ml-2 text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={calculateDuty}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              Calculate Stamp Duty
            </button>
          </div>

          {/* Results Section */}
          {result && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h3 className="text-xl font-semibold">Vehicle Stamp Duty Results</h3>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-gray-700">Stamp Duty Rate:</span>
                    <span className="font-semibold">{result.rate}</span>
                  </div>
                  
                  {result.discountAmount > 0 && (
                    <>
                      <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                        <span className="text-gray-700">Base Duty Amount:</span>
                        <span className="font-semibold">{formatCurrency(result.baseAmount)}</span>
                      </div>
                      <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                        <span className="text-gray-700">Concession Discount:</span>
                        <span className="font-semibold text-green-600">−{formatCurrency(result.discountAmount)}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                    <span className="text-xl font-bold text-gray-800">Final Stamp Duty Payable:</span>
                    <span className="text-xl font-bold text-blue-600">{formatCurrency(result.dutyAmount)}</span>
                  </div>
                  
                  {/* Explanation Section */}
                  <div className="mt-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Calculation Details:</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {result.explanation.map((item, index) => (
                        <li key={index} className="text-sm text-gray-600">{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Information Box */}
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">About Vehicle Stamp Duty in Australia</h3>
            <p className="text-gray-600 mb-4">
              Vehicle stamp duty is a state/territory government tax applied when registering or transferring ownership of a vehicle. 
              The amount varies significantly across Australia, depending on:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4">
              <li className="text-gray-600">The state or territory where the vehicle is registered</li>
              <li className="text-gray-600">The vehicle's market value or purchase price</li>
              <li className="text-gray-600">The type of vehicle (passenger, commercial, motorcycle)</li>
              <li className="text-gray-600">Engine characteristics (cylinders, emissions rating)</li>
              <li className="text-gray-600">Whether the vehicle is new or used</li>
              <li className="text-gray-600">Eligibility for exemptions or concessions</li>
            </ul>
            
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Stamp Duty Across Australian States & Territories</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium">New South Wales (NSW)</h5>
                  <p className="text-sm text-gray-600">3% up to $45,000; 5% above $45,000. Electric vehicles are exempt from stamp duty until June 30, 2027.</p>
                </div>
                <div>
                  <h5 className="font-medium">Victoria (VIC)</h5>
                  <p className="text-sm text-gray-600">Progressive rates from 4.2% to 9% based on vehicle value. Electric vehicles are exempt until June 30, 2025.</p>
                </div>
                <div>
                  <h5 className="font-medium">Queensland (QLD)</h5>
                  <p className="text-sm text-gray-600">Rates vary from 2% to 6% based on cylinder count and vehicle value. Hybrid and electric vehicles receive discounted rates.</p>
                </div>
                <div>
                  <h5 className="font-medium">Western Australia (WA)</h5>
                  <p className="text-sm text-gray-600">2.75% up to $50,000; 6.5% above $50,000. $3,000 discount for electric vehicles.</p>
                </div>
                <div>
                  <h5 className="font-medium">South Australia (SA)</h5>
                  <p className="text-sm text-gray-600">Progressive rates: 1% to $3,000; 4% to $60,000; 5% above $60,000. Electric vehicles are exempt until June 30, 2027.</p>
                </div>
                <div>
                  <h5 className="font-medium">Tasmania (TAS)</h5>
                  <p className="text-sm text-gray-600">Flat 3% rate on vehicle value. Electric vehicle exemption has expired (June 30, 2023).</p>
                </div>
                <div>
                  <h5 className="font-medium">Australian Capital Territory (ACT)</h5>
                  <p className="text-sm text-gray-600">Based on CO₂ emissions: 0% for lowest emissions to 4% for highest. Electric vehicles exempt until June 30, 2024.</p>
                </div>
                <div>
                  <h5 className="font-medium">Northern Territory (NT)</h5>
                  <p className="text-sm text-gray-600">Flat 3% rate on vehicle value. $1,500 discount for electric vehicles.</p>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600">
              This calculator provides the most current stamp duty estimates based on the information you provide. 
              Always verify the exact amount with your local motor vehicle registry or revenue office.
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">Frequently Asked Questions</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-700">What is vehicle stamp duty?</h4>
                <p className="text-gray-600 mt-2">
                  Vehicle stamp duty is a state or territory government tax you must pay when buying, transferring ownership, 
                  or registering a new or used vehicle in Australia. It's calculated based on the value of the vehicle and 
                  varies significantly depending on which state or territory you're registering in.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">How is vehicle stamp duty calculated?</h4>
                <p className="text-gray-600 mt-2">
                  The calculation method varies by state/territory. Some use a flat percentage rate (e.g., 3% in Tasmania), 
                  while others use tiered rates that increase with the vehicle's value (e.g., NSW charges 3% up to $45,000 
                  and 5% above that). Queensland bases rates on cylinder count, and the ACT uses CO₂ emissions categories. 
                  Our calculator automatically applies the correct formula based on your location.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">When do I need to pay stamp duty on a vehicle?</h4>
                <p className="text-gray-600 mt-2">
                  You typically need to pay stamp duty when:
                </p>
                <ul className="list-disc pl-5 mt-2 text-gray-600">
                  <li>Purchasing a new vehicle</li>
                  <li>Buying a used vehicle from a dealer or private seller</li>
                  <li>Transferring a vehicle into your name</li>
                  <li>Registering an interstate vehicle in your state</li>
                </ul>
                <p className="text-gray-600 mt-2">
                  Payment is usually made to your state or territory road transport authority at the time of registration or transfer.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">Are electric vehicles (EVs) exempt from stamp duty?</h4>
                <p className="text-gray-600 mt-2">
                  Many states offer exemptions or discounts for electric vehicles:
                </p>
                <ul className="list-disc pl-5 mt-2 text-gray-600">
                  <li>NSW: Full exemption until June 30, 2027</li>
                  <li>VIC: Full exemption until June 30, 2025</li>
                  <li>QLD: Reduced rates for EVs and hybrids</li>
                  <li>WA: $3,000 discount on duty</li>
                  <li>SA: Full exemption until June 30, 2027</li>
                  <li>TAS: Exemption expired on June 30, 2023</li>
                  <li>ACT: Full exemption until June 30, 2024, and zero-rated in lowest emissions category</li>
                  <li>NT: $1,500 discount on duty</li>
                </ul>
                <p className="text-gray-600 mt-2">
                  These incentives are subject to change, so it's important to check the latest policies.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">What concessions or exemptions are available for stamp duty?</h4>
                <p className="text-gray-600 mt-2">
                  Common exemptions or concessions across most states include:
                </p>
                <ul className="list-disc pl-5 mt-2 text-gray-600">
                  <li>Disability vehicle modifications or specialized disability vehicles</li>
                  <li>Family transfers (e.g., transfer between spouses or family members)</li>
                  <li>Primary producers (in some states for eligible farm vehicles)</li>
                  <li>Pensioner concessions (varies by state)</li>
                  <li>Electric vehicle incentives as detailed above</li>
                </ul>
                <p className="text-gray-600 mt-2">
                  Select any applicable concessions in our calculator to see potential savings.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">Is stamp duty negotiable or can I avoid paying it?</h4>
                <p className="text-gray-600 mt-2">
                  Stamp duty is a government tax and is not negotiable. Attempting to undervalue a vehicle to reduce 
                  stamp duty is illegal and may result in penalties. However, you can legitimately reduce stamp duty by:
                </p>
                <ul className="list-disc pl-5 mt-2 text-gray-600">
                  <li>Checking if you qualify for any exemptions or concessions</li>
                  <li>Purchasing an electric or hybrid vehicle in states with incentives</li>
                  <li>Purchasing a vehicle in a state with lower stamp duty rates (if you have this flexibility)</li>
                  <li>Choosing a lower-priced vehicle or one with favorable characteristics for your state (e.g., lower emissions in ACT)</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">How accurate is this stamp duty calculator?</h4>
                <p className="text-gray-600 mt-2">
                  Our calculator uses the current stamp duty rates and formulas for all Australian states and territories. 
                  However, rates and policies can change, and individual circumstances may vary. The calculated amount should 
                  be considered an estimate rather than the final amount payable. Always verify the exact amount with your 
                  state or territory road transport authority or revenue office.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700">Do I need to pay stamp duty if I'm importing a vehicle from overseas?</h4>
                <p className="text-gray-600 mt-2">
                  Yes, when registering an imported vehicle in Australia, you'll need to pay stamp duty based on the 
                  state or territory where you're registering it. This is in addition to any import duties, GST, 
                  luxury car tax, and other import-related costs. The stamp duty is calculated on the vehicle's value, 
                  typically as determined by the relevant state authority.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default StampDutyCalculator; 